import { isNaN } from 'lodash-es';
import type { GetServerSideProps } from 'next';
import { useTranslation } from 'next-i18next';
import type { ReactElement } from 'react';
import { ResponseStatus } from '../../../resources/@types/schema';
import {
  API_ENDPOINT,
  type ClientApiV1NotesIdWithUnitsResponse,
  fetchClientApiV1NotesIdWithUnits,
} from '../../../resources/@types/uccSchema';
import { ApiError } from '../../../resources/ApiError';
import AppShellLayoutForSSR from '../../components/layout/AppShellLayoutForSSR';
import CustomHead from '../../components/layout/CustomHead';
import { NoteDetailEmptyStates } from '../../components/pages/notes/EmptyStates';
import NoteDetail from '../../components/pages/notes/NoteDetail';
import useNoteIdWithUnitsQuery from '../../components/pages/notes/hooks/queries/useNoteIdWithUnitsQuery';
import { useTrackPageViewOnFirstEnter } from '../../components/pages/notes/hooks/useTrackNoteDetailMixpanelEvent';
import useRouterQueryId from '../../hooks/useRouterQueryId';
import { generateQueryKeyUrl } from '../../utils/generateQueryKeyUrl';
import { getCdnTranslationProps } from '../../utils/server/i18n/getCdnTranslationProps';

type Props = {
  serverNoteData?: ClientApiV1NotesIdWithUnitsResponse;
  isForbidden?: boolean;
};

export default function NoteDetailPage({ serverNoteData, isForbidden }: Props) {
  const { t } = useTranslation();

  const courseNoteId = useRouterQueryId();

  const { data: noteData, isLoading } = useNoteIdWithUnitsQuery(
    { id: courseNoteId },
    { initialData: serverNoteData },
  );

  const writeName = noteData?.writer.name;
  const courseTitle = noteData?.course.title;

  useTrackPageViewOnFirstEnter();

  if (!noteData && isLoading) {
    return null;
  }

  if (!noteData && isForbidden) {
    return <NoteDetailEmptyStates />;
  }

  return (
    <>
      <CustomHead
        title={t('{{writeName}}님의 {{courseTitle}} 학습노트', {
          writeName,
          courseTitle,
        })}
        description={t(
          '{{writeName}}님이 직접 기록하고 공부한 [{{courseTitle}}] 학습노트 입니다.',
          {
            writeName,
            courseTitle,
          },
        )}
      />
      <NoteDetail />
    </>
  );
}

NoteDetailPage.getLayout = (page: ReactElement) => (
  <AppShellLayoutForSSR footer={{ isVisible: false, hasBottomNavigation: true }}>
    {page}
  </AppShellLayoutForSSR>
);

export const getServerSideProps: GetServerSideProps = async (context) => {
  const id = Number(context.params?.id);
  const locale = context.locale as string;

  if (isNaN(id) || !Number.isInteger(id)) {
    return {
      notFound: true,
    };
  }

  try {
    const v2NoteIdWithUnitsResponse = await fetchClientApiV1NotesIdWithUnits(
      // TODO 쿼리 랩핑 후 함수로 추출 필요
      generateQueryKeyUrl({
        url: API_ENDPOINT.CLIENT_V1_NOTES_ID_WITH_UNITS(String(id)),
        queryParams: { lang: locale },
      }).url,
    );

    return {
      props: {
        ...(await getCdnTranslationProps(locale, ['common'])),
        serverNoteData: v2NoteIdWithUnitsResponse,
      },
    };
  } catch (error) {
    if (error instanceof ApiError) {
      if (error.statusCode === ResponseStatus.FORBIDDEN) {
        return {
          props: {
            ...(await getCdnTranslationProps(locale, ['common'])),
            isForbidden: true,
          },
        };
      }

      if (error.statusCode === ResponseStatus.NOT_FOUND) {
        return {
          notFound: true,
        };
      }
    }

    return {
      notFound: true,
    };
  }
};
